<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    @click.self="$emit('close')"
  >
    <div
      class="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden h-full"
      style="max-height: 90vh"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0"
      >
        <div class="flex items-center gap-2 min-w-0">
          <Icon :icon="fileIcon" class="h-5 w-5 text-gray-500 shrink-0" />
          <span class="font-medium text-gray-900 truncate">{{
            file.name
          }}</span>
          <span class="text-xs text-gray-400 shrink-0">{{
            file.file_type
          }}</span>
        </div>
        <button
          @click="$emit('close')"
          class="ml-3 p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors shrink-0"
        >
          <Icon icon="mdi:close" class="h-5 w-5" />
        </button>
      </div>

      <!-- Visualizer body -->
      <div class="flex-1 min-h-0 overflow-hidden">
        <ImageViewer
          v-if="visualizerType === 'image'"
          :name="file.name"
          :file="fileUrl"
        />
        <PDFViewer
          v-else-if="visualizerType === 'pdf'"
          :name="file.name"
          :file="fileUrl"
        />
        <CodeViewer
          v-else-if="visualizerType === 'code'"
          :name="file.name"
          :file="fileUrl"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted } from "vue";
import { Icon } from "@iconify/vue";
import { useApplicationStore } from "@/stores/ApplicationStore";
import ImageViewer from "@/components/Visualizers/ImageViewer.vue";
import PDFViewer from "@/components/Visualizers/PDFViewer.vue";
import CodeViewer from "@/components/Visualizers/CodeViewer.vue";

const props = defineProps({
  file: { type: Object, required: true },
  investigationId: { type: String, required: true },
  investigationName: { type: String, default: "" },
});

const emit = defineEmits(["close"]);

const FILE_TYPE_TO_VISUALIZER = {
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  webp: "image",
  bmp: "image",
  svg: "image",
  ico: "image",
  avif: "image",
  heic: "image",
  heif: "image",
  tif: "tiff",
  tiff: "tiff",
  pdf: "pdf",
  py: "code",
  js: "code",
  mjs: "code",
  ts: "code",
  tsx: "code",
  jsx: "code",
  vue: "code",
  html: "code",
  htm: "code",
  css: "code",
  scss: "code",
  less: "code",
  json: "code",
  yaml: "code",
  yml: "code",
  xml: "code",
  md: "code",
  txt: "code",
  csv: "code",
  sh: "code",
  bash: "code",
  r: "code",
  jl: "code",
  cpp: "code",
  cxx: "code",
  c: "code",
  h: "code",
  java: "code",
  rs: "code",
  go: "code",
  rb: "code",
  php: "code",
  toml: "code",
  ini: "code",
  cfg: "code",
  conf: "code",
  env: "code",
  sql: "code",
  tex: "code",
};

const visualizerType = computed(
  () => FILE_TYPE_TO_VISUALIZER[props.file?.file_type?.toLowerCase()] ?? null,
);

const fileUrl = computed(
  () =>
    `/enodo/api/investigation/${
      props.investigationId
    }/file?relative_path=${encodeURIComponent(
      props.file.path,
    )}&investigation_name=${encodeURIComponent(props.investigationName)}`,
);

const app = useApplicationStore();
app.current_selected = { path: fileUrl.value, name: props.file.name };

onUnmounted(() => {
  app.current_selected = null;
});

const FILE_ICONS = {
  image: "mdi:image-outline",
  pdf: "mdi:file-pdf-box",
  tiff: "mdi:image-multiple-outline",
  code: "mdi:code-tags",
};

const fileIcon = computed(
  () => FILE_ICONS[visualizerType.value] ?? "mdi:file-outline",
);

function onKeydown(e) {
  if (e.key === "Escape") emit("close");
}

onMounted(() => document.addEventListener("keydown", onKeydown));
onUnmounted(() => document.removeEventListener("keydown", onKeydown));
</script>
